import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon, ClipboardDocumentCheckIcon, TicketIcon } from '@heroicons/react/16/solid';
import { useEffect, useState } from 'react';

import { dispatchShowAlert } from '@/renderer/utils/alerts';
import { request } from '@/renderer/utils/api';
import copyToClipboard from '@/renderer/utils/copyToClipboard';
import formatReleaseLine from '@/renderer/utils/formatReleaseLine';
import useMe from '@/renderer/utils/useMe';
import type { RequestSchema } from '@/types/request';

// Admin/staff-only convenience (#1758): copy the current PENDING plays to the
// clipboard so an admin can paste them straight into a support-chat reply. Plays
// are released days ahead of their games, so this sources from ALL currently
// pending releases (/stats/releases/pending), NOT the gameTime-today panel source
// — otherwise a Thursday copy of the weekend slate would catch only the lone
// Thursday-night game. The endpoint carries the team/sport graph formatReleaseLine
// needs and already scopes to result === 'pending' (season-long futures excluded).
//
// The copied text is tab-delimited, one play per line, no header, so it drops
// straight into the admin's paste target:
//   CFB\t209 Baylor/Auburn Over 58
//   NFL\t303 Jets/Bills Under 44 (1.5u)
//
// The main button copies ALL pending plays in one click; the caret opens a menu to
// copy just one sport at a time (an admin sometimes only wants, say, the CFB slate).
const PENDING_ENDPOINT = '/api/v1.1/admin/stats/releases/pending';

/** Sport label for a release, used both to prefix a line and to group by sport. */
function sportLabelOf(r: API.Release): string {
  return r.sport?.shortName || r.sport?.name || 'Other';
}

/**
 * One pending play as a tab-delimited line: "<sport>\t<formatted line>", e.g.
 * "CFB\t301 Kansas City -3 at Denver". A non-default unit rating is appended in the
 * format-release-line style (" (1.5u)"); the price already rides along inside
 * formatReleaseLine (it shows "(-120)" etc. whenever price !== -110).
 */
export function pendingPlayLine(r: API.Release): string {
  const line = formatReleaseLine(r, false, true);
  const units = r.rating !== 1 ? ` (${r.rating}u)` : '';
  return `${sportLabelOf(r)}\t${line}${units}`;
}

/**
 * Build the shareable text block for the given pending releases — one tab-delimited
 * line per play, no header. Each line already carries its own sport prefix, so a
 * single-sport copy needs no extra labeling.
 */
export function buildPendingPlaysText(pending: API.Release[]): string {
  return pending.map(pendingPlayLine).join('\n');
}

/** Group pending releases by sport, preserving the order sports first appear. */
export function groupPendingBySport(
  pending: API.Release[],
): { label: string; plays: API.Release[] }[] {
  const map = new Map<string, API.Release[]>();
  for (const r of pending) {
    const label = sportLabelOf(r);
    const bucket = map.get(label);
    if (bucket) {
      bucket.push(r);
    } else {
      map.set(label, [r]);
    }
  }
  return [...map.entries()].map(([label, plays]) => ({ label, plays }));
}

/** Fetch all currently-pending releases. Throws on request failure. */
async function loadPending(): Promise<API.Release[]> {
  const res = (await request(
    PENDING_ENDPOINT as unknown as RequestSchema<{ data: API.Release[] }>,
  )) as { data?: API.Release[] } | undefined;
  return res?.data ?? [];
}

/**
 * Drop the given plays (already filtered) into the reply and toast the outcome.
 * When an `onInsert` is supplied (the composer is mounted), the text goes straight
 * into the draft — no clipboard round trip. Falls back to a clipboard copy if the
 * composer can't take the text (or no callback was given at all).
 */
async function copyPlays(
  plays: API.Release[],
  sportLabel?: string,
  onInsert?: (text: string) => boolean,
): Promise<void> {
  if (plays.length === 0) {
    dispatchShowAlert({
      message: sportLabel
        ? `No pending ${sportLabel} plays right now`
        : 'No pending plays right now',
      status: 'info',
    });
    return;
  }

  const text = buildPendingPlaysText(plays);
  const noun = plays.length === 1 ? 'play' : 'plays';

  if (onInsert?.(text)) {
    dispatchShowAlert({
      message: `Inserted ${plays.length} pending ${sportLabel ? `${sportLabel} ` : ''}${noun}`,
      status: 'success',
    });
    return;
  }

  const ok = await copyToClipboard(text);
  if (ok) {
    dispatchShowAlert({
      message: `Copied ${plays.length} pending ${sportLabel ? `${sportLabel} ` : ''}${noun}`,
      status: 'success',
    });
  } else {
    dispatchShowAlert({ message: 'Could not copy to clipboard', status: 'error' });
  }
}

/**
 * The per-sport menu, rendered inside the popover panel. Fetches on mount (i.e. when
 * the popover opens) and lists "All" plus one row per sport with its count. Picking a
 * row copies just that subset and closes the popover.
 */
function SportMenu({
  onPicked,
  onInsert,
}: {
  onPicked: () => void;
  onInsert?: (text: string) => boolean;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState<{ label: string; plays: API.Release[] }[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let alive = true;
    loadPending()
      .then((pending) => {
        if (!alive) {
          return;
        }
        setTotal(pending.length);
        setGroups(groupPendingBySport(pending));
        setLoading(false);
      })
      .catch((err: Error) => {
        if (!alive) {
          return;
        }
        setError(err?.message || 'Could not load pending plays');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const pick = async (plays: API.Release[], sportLabel?: string) => {
    onPicked();
    await copyPlays(plays, sportLabel, onInsert);
  };

  if (loading) {
    return <div className="px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">Loading…</div>;
  }
  if (error) {
    return <div className="px-3 py-2 text-xs text-rose-600 dark:text-rose-400">{error}</div>;
  }
  if (total === 0) {
    return (
      <div className="px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">No pending plays</div>
    );
  }

  const rowClass =
    'flex w-full items-center justify-between gap-4 px-3 py-1.5 text-left text-xs text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800';

  return (
    <div className="py-1">
      <button
        type="button"
        className={`${rowClass} font-medium`}
        onClick={() => pick(groups.flatMap((g) => g.plays))}
      >
        <span>All sports</span>
        <span className="text-zinc-400 dark:text-zinc-500">{total}</span>
      </button>
      <div className="my-1 border-zinc-950/5 border-t dark:border-white/5" />
      {groups.map((g) => (
        <button
          type="button"
          key={g.label}
          className={rowClass}
          onClick={() => pick(g.plays, g.label)}
        >
          <span>{g.label}</span>
          <span className="text-zinc-400 dark:text-zinc-500">{g.plays.length}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * "Copy pending plays" split button for the support-chat composer. Admin/staff only —
 * renders nothing for members. The main button copies ALL pending plays; the caret
 * opens a menu to copy a single sport at a time.
 */
export default function CopyPendingPlaysButton({
  onInsert,
}: {
  // Insert the snippet straight into the composer draft; returns false if that
  // isn't possible so we fall back to a clipboard copy (#1817 follow-up).
  onInsert?: (text: string) => boolean;
} = {}) {
  const me = useMe();
  const isStaffViewer = me?.role === 'admin' || me?.role === 'staff';
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Label reflects the primary action: insert into the composer when wired up,
  // otherwise the legacy clipboard copy.
  const doneLabel = onInsert ? 'Inserted' : 'Copied';

  // Gate on role — a member never sees this, and it self-gates even if dropped
  // somewhere its parent forgot to check the role.
  if (!isStaffViewer) {
    return null;
  }

  const copyAll = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      const pending = await loadPending();
      await copyPlays(pending, undefined, onInsert);
      if (pending.length > 0) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      dispatchShowAlert({
        message: (err as Error)?.message || 'Could not load pending plays',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  // One split control: a main button + a caret column, drawn as a single rounded,
  // ring-outlined unit that matches the composer's other controls. The outline lives
  // in a `pointer-events-none` overlay ABOVE the buttons rather than as an inset ring
  // on the container — a child's hover background would otherwise paint over an inset
  // ring and nibble the border. The caret's `border-l` is interior (never at the
  // rounded perimeter the overlay draws), so it stays crisp under either button's hover.
  return (
    <div className="relative inline-flex items-stretch rounded-md text-xs font-medium text-zinc-600 dark:text-zinc-300">
      <button
        type="button"
        onClick={copyAll}
        disabled={busy}
        title={
          onInsert
            ? 'Insert all pending plays into your reply'
            : 'Copy all pending plays to your clipboard'
        }
        aria-label={
          onInsert
            ? 'Insert all pending plays into your reply'
            : 'Copy all pending plays to your clipboard'
        }
        className="inline-flex items-center gap-1.5 rounded-l-md px-2 py-1 hover:bg-zinc-100 disabled:opacity-50 dark:hover:bg-zinc-800"
      >
        {copied ? (
          <ClipboardDocumentCheckIcon className="size-3.5 shrink-0 text-emerald-500" />
        ) : (
          <TicketIcon className="size-3.5 shrink-0" />
        )}
        {/*
          Icon-only on small screens to save composer real estate (the tooltip /
          aria-label carry the meaning); the label shows from sm up. `whitespace-nowrap`
          guarantees it never breaks into stacked lines — a narrow parent wraps the whole
          control to its own line instead.
        */}
        <span className="hidden whitespace-nowrap sm:inline">
          {copied ? doneLabel : busy ? 'Working…' : 'Pending plays'}
        </span>
      </button>
      <Popover as="div" className="relative flex">
        <PopoverButton
          title="Copy one sport at a time"
          aria-label="Copy one sport at a time"
          className="flex w-7 items-center justify-center self-stretch rounded-r-md border-zinc-950/10 border-l hover:bg-zinc-100 focus:outline-none dark:border-white/10 dark:hover:bg-zinc-800"
        >
          <ChevronDownIcon className="size-3.5 shrink-0" />
        </PopoverButton>
        <PopoverPanel
          transition
          // `anchor` portals the panel to the document root and positions it with
          // collision handling (flips/shifts to stay on screen), so it no longer gets
          // clipped by the composer's overflow when the toolbar is near a window edge.
          // Opens above the caret ("top end"); Floating UI flips it below if there's no
          // room. Replaces the old absolute `bottom-full` positioning.
          anchor={{ to: 'top end', gap: 8, padding: 8 }}
          className="z-20 max-h-72 w-44 origin-bottom-right overflow-auto rounded-md bg-white shadow-lg ring-1 ring-zinc-950/10 transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-zinc-900 dark:ring-white/10"
        >
          {({ close }) => <SportMenu onPicked={close} onInsert={onInsert} />}
        </PopoverPanel>
      </Popover>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-zinc-950/10 ring-inset dark:ring-white/10"
      />
    </div>
  );
}
